/**
 * Messaging Domain Store
 *
 * Manages conversations and messages between startups and investors.
 */

import { create } from 'zustand';
import type { TeamMemberRole } from './profile-store';

// ============================================================================
// Types
// ============================================================================

export interface Message {
  id: string;
  conversationId: string;
  senderId: string;
  senderName: string;
  senderType: 'startup' | 'investor';
  senderRole?: TeamMemberRole;
  text: string;
  timestamp: string;
}

export interface Conversation {
  id: string;
  startupId: string;
  startupName: string;
  /**
   * 119-07: the `startup_profiles(name)` embed came back NULL, so
   * `startupName` is only the 'Startup' placeholder. A SEALED company's profile
   * is unreadable to every client; the list fills the name from its public
   * card (`withPublicCardNames`).
   */
  startupNameMissing?: boolean;
  /**
   * `conversations.investor_profile_id`. NULL once the investor has erased
   * their account: the FK is ON DELETE SET NULL so the founder's side of the
   * thread survives (20261015120000_erasure_fk_integrity.sql). Such a thread is
   * read-only — see `isInvestorErased`.
   */
  investorId: string | null;
  /**
   * Same value as `investorId` today (both come from
   * `conversations.investor_profile_id` — see mapConversationRow). Kept as
   * its own field because it's the name the block/report pair
   * ({ startupId, investorProfileId }, see src/lib/moderation/api.ts) expects;
   * `investorId` stays for the existing presence/display call sites.
   */
  investorProfileId: string | null;
  investorName: string;
  /** Server-maintained per-side read marks, from `conversations`. The
   *  COUNTERPARTY's one decides Sent vs Read. */
  startupLastReadAt?: string | null;
  investorLastReadAt?: string | null;
  lastMessage?: string;
  lastMessageTime?: string;
  unreadCount: number;
  status: 'active' | 'archived';
}

// ============================================================================
// Store Interface
// ============================================================================

interface MessagingState {
  conversations: Conversation[];
  setConversations: (convos: Conversation[]) => void;
  messages: Message[];
  setMessages: (msgs: Message[]) => void;
  addMessage: (msg: Message) => void;
  markConversationRead: (conversationId: string) => void;
  startConversation: (conversation: Conversation) => void;

  reset: () => void;
}

// ============================================================================
// Initial State
// ============================================================================

const initialState = {
  conversations: [] as Conversation[],
  messages: [] as Message[],
};

// ============================================================================
// Store
// ============================================================================

export const useMessagingStore = create<MessagingState>()(
  (set, get) => ({
    ...initialState,

    setConversations: (convos) => set({ conversations: convos }),
    setMessages: (msgs) => set({ messages: msgs }),

    addMessage: (msg) => {
      const conversations = get().conversations.map(c =>
        c.id === msg.conversationId
          ? {
              ...c,
              lastMessage: msg.text,
              lastMessageTime: msg.timestamp,
              unreadCount: msg.senderType === 'investor' ? c.unreadCount + 1 : c.unreadCount,
            }
          : c
      );
      set({
        messages: [...get().messages, msg],
        conversations,
      });
    },

    // Clears the badge only. A message no longer carries a `read` flag: the
    // receipt is derived from the counterparty's read marks, so there is nothing
    // per-message for a local action to set — and stamping one here would let the
    // client assert that the OTHER side had read something, which is exactly the
    // forgery the server-side guard refuses.
    markConversationRead: (conversationId) => set({
      conversations: get().conversations.map(c =>
        c.id === conversationId ? { ...c, unreadCount: 0 } : c
      ),
    }),

    startConversation: (conversation) => set({
      conversations: [...get().conversations, conversation],
    }),

    reset: () => set(initialState),
  })
);
